import React, { useContext, useEffect } from 'react';
import { MantineProvider, Stack } from '@mantine/core';
import { useRouter } from 'next/router';

import { FEATURE } from '~/common/enums/feature.enum';
import UnknownErrorScreen from '~/domains/common/components/UnknownErrorScreen/UnknownErrorScreen';
import HeadTitle from '~/domains/common/components/HeadTitle';
import { getContestThumbnail } from '~/domains/contest/domains/common/utils/getContestThumbnail';
import useIsContestCommissioner from '~/domains/commissioner/hooks/useIsContestCommissioner';
import { useShowCommissionerEmailListPopup } from '~/domains/common/hooks/useShowCommissionerEmailListPopup';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';

import { useContest } from '../../../../hooks/useContest';
import { isApiError } from '../../../../../services/utils/isApiError';
import ContestDetail from '../../components/ContestDetail/ContestDetail';
import ContestDetailSkeleton from '../../components/ContestDetail/ContestDetailSkeleton';
import { EntryNameEditModalContextProvider } from '../../../gamecenter/domains/common/context/EntryNameEditModalContext/EntryNameEditModalContext';

export function ContestDetailPage() {
  const { query } = useRouter();
  const { openModal } = useContext(RootModalsContext);
  const fullContest = useContest();
  const isContestCommissioner = useIsContestCommissioner(fullContest?.data);
  const showCommissionerEmailListPopup = useShowCommissionerEmailListPopup();
  const isEmailPopupEnabled = useIsFeatureEnabled(
    FEATURE.ENABLE_COMMISSIONER_LIST_INTERCOM__CONTEST_DETAILS
  );

  useEffect(() => {
    if (query[URLQueryKeys.ENTER_CONTEST] === 'true') {
      openModal({
        type: 'EnterContestModal',
        contestId: query.contestUuid as string,
      });
    }
  }, []);

  if (isContestCommissioner && isEmailPopupEnabled) {
    showCommissionerEmailListPopup();
  }

  return (
    <MantineProvider withNormalizeCSS withGlobalStyles inherit theme={{ colorScheme: 'light' }}>
      <HeadTitle />
      <EntryNameEditModalContextProvider>
        <Stack spacing={0} sx={{ minHeight: '100%' }}>
          {(() => {
            if (fullContest.isLoading) {
              return <ContestDetailSkeleton />;
            }

            if (fullContest.error && isApiError(fullContest.error)) {
              return <UnknownErrorScreen statusCode={fullContest.error.response.status} />;
            }

            return (
              <>
                <HeadTitle title={fullContest.data.name}>
                  {fullContest && (
                    <>
                      <meta property="og:type" content="website" />
                      <meta
                        property="og:url"
                        content={`${window.location.origin}/contest/${fullContest.data.id}`}
                      />
                      <meta
                        property="og:image"
                        content={getContestThumbnail({
                          contestThumbnailUrl: fullContest.data?.thumbnail_url,
                          createdByAdmin: fullContest.data?.created_by_admin,
                          avatarUrl: fullContest.data?.commissioner.avatarUrl,
                        })}
                      />
                    </>
                  )}
                </HeadTitle>
                <ContestDetail />
              </>
            );
          })()}
        </Stack>
      </EntryNameEditModalContextProvider>
    </MantineProvider>
  );
}
